/**
 * ErrorBoundary — catches render-time exceptions in the routed page tree and
 * shows a recoverable message instead of a blank screen.
 *
 * 2026-06-03: a single null field (a campaign with no synced daily_budget)
 * threw inside a table renderer and, with no boundary anywhere in the app,
 * unmounted the WHOLE React tree → blank page with no way back. This boundary
 * keeps the header + sidebar alive and offers a reload, and surfaces the error
 * text so the failure is diagnosable instead of silent. `resetKey` (wired to
 * the route path) clears the error on navigation so one bad page doesn't wedge
 * the whole session.
 */
import { Component, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

type Props = { children: ReactNode; resetKey?: string }
type State = { error: Error | null; componentStack: string | null }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null }

  static getDerivedStateFromError(error: Error): Pick<State, 'error'> {
    return { error }
  }

  /** A failed dynamic import means this browser is holding an index.html that
   *  points at chunks the current deploy no longer has -- vite wipes dist/ on
   *  every build, so the hashed file it wants is a hard 404. AppRoutes lazy()s
   *  27 routes, so the symptom is: the URL changes and the page never does.
   *
   *  The bytes to fix it are already on the server; this browser just cannot
   *  see them. One reload re-fetches index.html (now sent no-cache) and picks
   *  up the new hashes. Guarded by sessionStorage so a genuinely broken chunk
   *  cannot become a reload loop -- after one attempt we fall through to the
   *  normal fallback UI. */
  private static isChunkLoadError(error: Error): boolean {
    const msg = `${error?.name ?? ''} ${error?.message ?? ''}`
    return /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(msg)
  }

  componentDidUpdate(prev: Props) {
    // Navigating to a different route clears a stale error so the next page
    // renders fresh rather than staying stuck on the fallback.
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, componentStack: null })
    }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    if (ErrorBoundary.isChunkLoadError(error)) {
      const KEY = 'cp:chunk-reload'
      let already = false
      try { already = sessionStorage.getItem(KEY) === '1' } catch { /* private mode */ }
      if (!already) {
        try { sessionStorage.setItem(KEY, '1') } catch { /* ignore */ }
        console.warn('Stale bundle detected (chunk 404). Reloading once to pick up the current build.')
        window.location.reload()
        return
      }
    }
    // Keep a console trail for diagnosis (the fallback also shows the message).
    console.error('ErrorBoundary caught a render error:', error, info)
    // The message alone is often not enough to act on: React's PRODUCTION
    // build ships numeric codes ("Minified React error #185") whose text has
    // to be looked up, and which name the failure class but not the place.
    // `componentStack` is built from component NAMES so it survives
    // minification — surface it in the fallback so an operator who can't open
    // devtools can still screenshot something that identifies the component.
    this.setState({ componentStack: info?.componentStack ?? null })
  }

  render() {
    if (this.state.error) {
      return (
        <div className="p-8">
          <div className="max-w-lg mx-auto mt-12 bg-white border border-rose-200 rounded-lg shadow-sm p-6">
            <h1 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <AlertTriangle size={18} className="text-rose-600" />
              This page hit an error
            </h1>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              Something in this view failed to render. The rest of the app is fine — use the sidebar
              to go elsewhere, or reload to try again. If it keeps happening, the message below helps
              pin it down.
            </p>
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 mb-4 overflow-auto max-h-40 text-rose-800 whitespace-pre-wrap">
              {this.state.error.message || String(this.state.error)}
            </pre>
            {this.state.componentStack && (
              <details className="mb-4">
                <summary className="text-xs text-gray-500 cursor-pointer select-none">
                  Which component failed
                </summary>
                <pre className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-40 text-gray-700 whitespace-pre-wrap">
                  {this.state.componentStack.trim()}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => this.setState({ error: null, componentStack: null })}
                className="px-3 py-1.5 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-3 py-1.5 text-sm rounded-md bg-brand-600 text-white hover:bg-brand-700"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
